import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const AIRecommendationPopover = ({
  data = {},
  isOpen = false,
  onToggle = () => {},
  onClose = () => {},
  onPreview = () => {},
}) => {
  const { query = "", aiTexts = [] } = data;
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({});

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      const clickedTrigger = triggerRef.current?.contains(event.target);
      const clickedPopover = popoverRef.current?.contains(event.target);
      if (!clickedTrigger && !clickedPopover) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  return (
    <div className="d-inline-block">
      <style>{`
        @keyframes sparklePulse {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.85; }
          50% { transform: scale(1.15) rotate(6deg); opacity: 1; }
        }
        .ai-sparkle-btn:hover .ai-sparkle-icon {
          animation: sparklePulse 1.4s ease-in-out infinite;
        }
      `}</style>
      <button
        ref={triggerRef}
        type="button"
        className="btn p-0 border-0 bg-transparent text-decoration-none ai-sparkle-btn"
        onClick={onToggle}
        title="Show AI recommendations"
        aria-expanded={isOpen}
        aria-label={`Show AI recommendations for ${query}`}
      >
        <span
          className="ai-sparkle-icon"
          style={{
            display: "inline-flex",
            width: "28px",
            height: "28px",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            border: "1px solid #ffe39a",
            background: isOpen ? "#fff7e2" : "#fffdf5",
            color: "#b7791f",
            fontSize: "16px",
            fontWeight: "700",
            boxShadow: "0 4px 12px rgba(183, 121, 31, 0.16)",
          }}
        >
          ✨
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="card border-0"
            style={{
              ...popoverStyle,
              minWidth: "280px",
              maxWidth: "340px",
              boxShadow: "0 14px 30px rgba(15, 23, 42, 0.18)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <div
              className="card-body p-3"
              style={{
                background: "#ffffff",
                maxHeight: "220px",
                overflowY: "auto",
              }}
            >
              <div className="fw-semibold mb-2" style={{ color: "#1f2937" }}>
                AI Suggested Synonyms
              </div>
              {aiTexts.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {aiTexts.map((text, textIndex) => (
                    <span
                      key={`${query}-text-${textIndex}`}
                      className="badge"
                      style={{
                        background: "#f3f4f6",
                        color: "#111827",
                        fontWeight: 600,
                        padding: "6px 8px",
                      }}
                    >
                      {text}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="small text-muted">
                  No suggestions available.
                </div>
              )}
              <button
                type="button"
                onClick={() => onPreview(data)}
                disabled={aiTexts.length === 0}
                className="btn w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                style={{
                  background:
                    aiTexts.length > 0
                      ? "linear-gradient(135deg, #0f766e, #115e59)"
                      : "#d1d5db",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "0.2px",
                  padding: "9px 12px",
                  boxShadow:
                    aiTexts.length > 0
                      ? "0 8px 16px rgba(15, 118, 110, 0.28)"
                      : "none",
                  cursor: aiTexts.length > 0 ? "pointer" : "not-allowed",
                }}
                title="Preview products for all suggested synonyms"
              >
                {/* <span style={{ fontSize: "14px", lineHeight: 1 }}>▣</span> */}
                <i className="fa-solid fa-eye" />
                <span>Preview</span>
                <span
                  className="badge"
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#ffffff",
                    fontWeight: 700,
                  }}
                >
                  {aiTexts.length}
                </span>
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AIRecommendationPopover;
